import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { EXPANDED_ROWS_LIMIT } from 'src/constants/table';
import { useInfiniteDeviceWdacResults } from 'src/hooks/device';
import AppResultTable from './AppResultTable';
import { defaultTrustStatuses } from './constants';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';

const DEFAULT_LIST_FILTERS = { applicationTrustStatuses: [...defaultTrustStatuses] };
const DEFAULT_SORT = { column: 'lastSeen', direction: 'desc' };

const DeviceAppResultsSection = () => {
  const { deviceId } = useParams();

  const [searchValue, setSearchValue] = useState('');
  const [listFilters, setListFilters] = useState(
    () => readFilterParams(DEFAULT_LIST_FILTERS) ?? DEFAULT_LIST_FILTERS,
  );
  const [sort, setSort] = useState(DEFAULT_SORT);

  useFilterParams(listFilters);

  const apiQuery = useMemo(() => {
    const params = { SortColumn: sort.column, SortDesc: sort.direction === 'desc' };

    const trimmedSearch = typeof searchValue === 'string' ? searchValue.trim() : '';
    if (trimmedSearch) {
      params.Search = trimmedSearch;
    }

    const statuses = (listFilters?.applicationTrustStatuses || [])
      .map(value => String(value))
      .filter(Boolean);
    if (statuses.length > 0) {
      params.ApplicationTrustStatuses = statuses;
    }

    return params;
  }, [searchValue, sort, listFilters]);

  const wdacResultsQuery = useInfiniteDeviceWdacResults({
    deviceId,
    apiQuery,
    limit: EXPANDED_ROWS_LIMIT,
    enabled: !!deviceId,
  });

  const results = useMemo(
    () => wdacResultsQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [wdacResultsQuery.data],
  );
  const totalCount = useMemo(
    () => wdacResultsQuery.data?.pages?.[0]?.totalCount || 0,
    [wdacResultsQuery.data],
  );

  const loadMore = useCallback(() => {
    if (wdacResultsQuery.hasNextPage) {
      wdacResultsQuery.fetchNextPage();
    }
  }, [wdacResultsQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: wdacResultsQuery.isFetchingNextPage,
    hasNextPage: wdacResultsQuery.hasNextPage,
    disabled: !deviceId,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  return (
    <AppResultTable
      isLoading={wdacResultsQuery.isLoading}
      results={results}
      sentryRef={sentryRef}
      isFetchingNextPage={wdacResultsQuery.isFetchingNextPage}
      totalCount={totalCount}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      listFilters={listFilters}
      onListFiltersChange={setListFilters}
      sort={sort}
      onSortChange={setSort}
    />
  );
};

export default DeviceAppResultsSection;
